import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import {
  Phone, Shield, Clock, Languages, Award, Users, MapPin, Star, ArrowRight,
  ChevronRight, Menu, X, ScrollText, FileSignature, LogIn, Zap, Handshake,
  Landmark, HeartHandshake, CheckCircle2, Search,
} from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from '@studio-freight/lenis';

import SEO from '@/components/SEO';
import JailBarHero from '@/components/JailBarHero';
import MobileNavOverlay from '@/components/MobileNavOverlay';
import AmigosWordmark from '@/components/AmigosWordmark';
import { useLanguage } from '@/i18n/LanguageContext';
import { CITIES } from '@/data/cityContent';
import { track, captureAttribution } from '@/lib/analytics';

gsap.registerPlugin(ScrollTrigger);

/* ═════ Helpers ═════════════════════════════════════════════════ */
const Reveal = ({ children, delay = 0, y = 32 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.85, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Counter = ({ value, suffix = '', prefix = '', duration = 1800 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [out, setOut] = useState(prefix + '0' + suffix);
  useEffect(() => {
    if (!inView) return;
    const num = String(value).replace(/[^0-9.]/g, '');
    if (!num) { setOut(value); return; }
    const target = parseFloat(num);
    const start = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      const current = target < 10 ? (target * eased).toFixed(1) : Math.floor(target * eased);
      setOut(prefix + current + suffix);
      if (p < 1) requestAnimationFrame(tick); else setOut(prefix + value + suffix);
    };
    requestAnimationFrame(tick);
  }, [inView, value, suffix, prefix, duration]);
  return <span ref={ref}>{out}</span>;
};

const Marquee = ({ items }) => (
  <div className="cn-marquee" data-testid="cn-marquee">
    <div className="cn-marquee__track">
      {[...items, ...items].map((t, i) => (
        <span className="cn-marquee__item" key={i}>
          <span className="cn-marquee__dot" />
          {t}
        </span>
      ))}
    </div>
  </div>
);

/* ═════ Cinematic Nav (custom for home) ═════════════════════════ */
const CinematicNav = ({ lang, onLangSwitch }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const links = [
    { to: '/', en: 'Home', es: 'Inicio' },
    { to: '/how-bail-works', en: 'How Bail Works', es: 'Cómo Funciona' },
    { to: '/areas', en: 'Service Areas', es: 'Áreas' },
    { to: '/financing', en: 'Financing', es: 'Financiamiento' },
    { to: '/faq', en: 'FAQ', es: 'FAQ' },
    { to: '/about', en: 'About', es: 'Nosotros' },
  ];
  return (
    <>
      <nav className={`cn-nav ${scrolled ? 'cn-nav--scrolled' : ''}`} data-testid="cinematic-nav">
        <div className="cn-nav__inner">
          <Link to="/" className="cn-nav__brand" data-testid="nav-logo-link" style={{ textDecoration: 'none' }}>
            <AmigosWordmark size="md" subline="license" testId="nav-wordmark" />
          </Link>
          <div className="cn-nav__links">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="cn-nav__link" data-testid={`cn-nav-${l.en.toLowerCase().replace(/\s+/g, '-')}`}>
                {lang === 'es' ? l.es : l.en}
              </Link>
            ))}
            <Link to="/dashboard/login" className="cn-nav__signin" data-testid="cn-nav-signin">
              <LogIn size={13} /> {lang === 'es' ? 'Personal' : 'Sign In'}
            </Link>
            <Link to="/start-a-bond" className="cn-nav__cta" data-testid="cn-nav-cta">
              <FileSignature size={15} /> {lang === 'es' ? 'Iniciar Fianza' : 'Start a Bond'}
            </Link>
          </div>
          <button className="cn-nav__toggle" aria-label="Open menu" onClick={() => setOpen(true)} data-testid="cn-nav-mobile-toggle">
            <Menu size={20} />
          </button>
        </div>
      </nav>
      <MobileNavOverlay open={open} onClose={() => setOpen(false)} phoneDisplay="(936) 828-2500" phoneTel="9368282500" />
    </>
  );
};

/* ═════ Emergency Strip (24/7 top band) ═════════════════════════ */
const EmergencyStrip = () => {
  const { lang, switchLang } = useLanguage();
  return (
    <div className="emergency-strip" data-testid="emergency-strip">
      <div className="emergency-strip__inner">
        <span className="emergency-strip__label">
          {lang === 'es' ? 'FIANZAS 24/7 · CONDADO DE MONTGOMERY' : '24/7 BAIL BONDS · MONTGOMERY COUNTY'}
        </span>
        <a href="tel:9368282500" className="emergency-strip__phone" data-testid="emergency-call-link"
           onClick={() => track('phone_click', { location: 'emergency_strip' })}>
          (936) 828-2500
        </a>
        <div className="emergency-strip__lang" role="group" aria-label="Language">
          <button type="button" className={lang === 'en' ? 'active' : ''} onClick={() => switchLang('en')} data-testid="lang-en-btn">EN</button>
          <button type="button" className={lang === 'es' ? 'active' : ''} onClick={() => switchLang('es')} data-testid="lang-es-btn">ES</button>
        </div>
      </div>
    </div>
  );
};

/* ═════ Cinematic Footer (custom for home) ══════════════════════ */
const CinematicFooter = () => {
  const { lang } = useLanguage();
  const year = new Date().getFullYear();
  return (
    <footer className="cn-footer" data-testid="cn-footer">
      <div className="cn-footer__inner">
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <AmigosWordmark size="lg" subline="tagline" testId="footer-wordmark" />
          </div>
          <p className="cn-footer__desc">
            {lang === 'es'
              ? 'Ayuda de fianzas rápida y confidencial 24/7 para el Condado de Montgomery, Texas. Licencia #0111. Se habla Español.'
              : "Fast, confidential 24/7 bail bond help for Montgomery County, Texas. License #0111. Se habla Español."}
          </p>
          <a href="tel:9368282500" className="cn-footer__phone" data-testid="footer-phone">(936) 828-2500</a>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
            <MapPin size={13} /> Conroe · Montgomery County · TX
          </div>
        </div>
        <div>
          <h4 className="cn-footer__col-title">{lang === 'es' ? 'Navegación' : 'Navigate'}</h4>
          <Link to="/" className="cn-footer__link">{lang === 'es' ? 'Inicio' : 'Home'}</Link>
          <Link to="/how-bail-works" className="cn-footer__link">{lang === 'es' ? 'Cómo Funciona' : 'How Bail Works'}</Link>
          <Link to="/areas" className="cn-footer__link">{lang === 'es' ? 'Áreas de Servicio' : 'Service Areas'}</Link>
          <Link to="/financing" className="cn-footer__link">{lang === 'es' ? 'Financiamiento' : 'Financing'}</Link>
          <Link to="/faq" className="cn-footer__link">FAQ</Link>
        </div>
        <div>
          <h4 className="cn-footer__col-title">{lang === 'es' ? 'Recursos' : 'Resources'}</h4>
          <Link to="/start-a-bond" className="cn-footer__link">{lang === 'es' ? 'Iniciar Fianza' : 'Start a Bond'}</Link>
          <Link to="/montgomery-county-jail" className="cn-footer__link">Montgomery County Jail</Link>
          <Link to="/about" className="cn-footer__link">{lang === 'es' ? 'Sobre Nosotros' : 'About Us'}</Link>
          <Link to="/contact" className="cn-footer__link">{lang === 'es' ? 'Contacto' : 'Contact'}</Link>
          <Link to="/dashboard/login" className="cn-footer__link">{lang === 'es' ? 'Acceso del Personal' : 'Staff Portal'}</Link>
        </div>
        <div>
          <h4 className="cn-footer__col-title">{lang === 'es' ? 'Legal' : 'Legal'}</h4>
          <Link to="/privacy-policy" className="cn-footer__link">{lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}</Link>
          <Link to="/data-collection-policy" className="cn-footer__link">{lang === 'es' ? 'Política de Datos' : 'Data Collection'}</Link>
          <span className="cn-footer__link" style={{ color: 'var(--text-muted)' }}>{lang === 'es' ? 'Agente Licenciado' : 'Licensed Agent'}: Steve Cruz</span>
        </div>
      </div>
      <div className="cn-footer__bottom">
        <span>© {year} Amigo&apos;s Bail Bonds — {lang === 'es' ? 'Todos los derechos reservados' : 'All rights reserved'}. License #0111.</span>
        <span className="cn-footer__credit" data-testid="footer-dev-credit">
          Developed by James Hudson @ Texas IT Nerds | Protected by TITN Aegis Security
        </span>
      </div>
    </footer>
  );
};

/* ═════ MAIN — Cinematic Home ═══════════════════════════════════ */
const CinematicHome = () => {
  const { lang } = useLanguage();
  const gsapScope = useRef(null);

  useEffect(() => {
    captureAttribution();
    window.scrollTo(0, 0);
    track('page_view', { page: 'home_cinematic' }, '/');
  }, []);

  /* Lenis smooth scroll (respects reduced motion) */
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    return () => { lenis.destroy(); };
  }, []);

  /* GSAP staggered card reveals */
  useEffect(() => {
    if (!gsapScope.current) return;
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.cn-auth-card').forEach((card, i) => {
        gsap.fromTo(card, { y: 36, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.75, delay: i * 0.06,
          scrollTrigger: { trigger: card, start: 'top 88%' },
          ease: 'power3.out',
        });
      });
      gsap.utils.toArray('.cn-testimonial').forEach((card, i) => {
        gsap.fromTo(card, { y: 42, opacity: 0, scale: 0.97 }, {
          y: 0, opacity: 1, scale: 1, duration: 0.85, delay: i * 0.07,
          scrollTrigger: { trigger: card, start: 'top 90%' },
          ease: 'power3.out',
        });
      });
      gsap.utils.toArray('.cd-pipe__bar-fill').forEach((el) => {
        const w = el.dataset.width || '0';
        gsap.fromTo(el, { width: '0%' }, {
          width: `${w}%`, duration: 1.1, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 92%' },
        });
      });
    }, gsapScope);
    return () => ctx.revert();
  }, []);

  /* Scroll progress bar */
  useEffect(() => {
    const bar = document.getElementById('amg-scrollbar');
    if (!bar) return;
    const onScroll = () => {
      const h = document.documentElement;
      const p = (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100;
      bar.style.width = `${Math.max(0, Math.min(100, p))}%`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Section-header light sweep on entering viewport */
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.35, rootMargin: '0px 0px -10% 0px' });
    document.querySelectorAll('.cn-sec__title').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  /* Testimonial 3D cursor tilt */
  useEffect(() => {
    const cards = document.querySelectorAll('.cn-testimonial, .cn-auth-card');
    const handlers = [];
    cards.forEach((card) => {
      card.classList.add('tilt-3d');
      const glare = document.createElement('div');
      glare.className = 'tilt-3d__glare';
      card.appendChild(glare);
      const onMove = (e) => {
        const rect = card.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        const rx = ((mx / rect.width) - 0.5) * 8;
        const ry = ((my / rect.height) - 0.5) * -6;
        card.style.setProperty('--rx', `${rx}deg`);
        card.style.setProperty('--ry', `${ry}deg`);
        card.style.setProperty('--mx', `${mx}px`);
        card.style.setProperty('--my', `${my}px`);
      };
      const onLeave = () => {
        card.style.setProperty('--rx', `0deg`);
        card.style.setProperty('--ry', `0deg`);
      };
      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);
      handlers.push({ card, onMove, onLeave });
    });
    return () => {
      handlers.forEach(({ card, onMove, onLeave }) => {
        card.removeEventListener('mousemove', onMove);
        card.removeEventListener('mouseleave', onLeave);
      });
    };
  }, []);

  const trustChips = [
    { icon: <Shield size={12} />, en: 'LIC. #0111 · TEXAS', es: 'LIC. #0111 · TEXAS' },
    { icon: <Landmark size={12} />, en: 'MONTGOMERY CO. PROCESS', es: 'PROCESO M. COUNTY' },
    { icon: <Languages size={12} />, en: 'SE HABLA ESPAÑOL', es: 'SE HABLA ESPAÑOL' },
    { icon: <Clock size={12} />, en: '24/7 REAL PEOPLE', es: '24/7 PERSONAS REALES' },
  ];

  const marqueeItems = lang === 'es'
    ? ['CONDADO DE MONTGOMERY', 'LIC #0111', 'DISPONIBLE 24/7', 'BILINGÜE', 'PAGOS FLEXIBLES', 'STEVE CRUZ', 'RESPUESTA RÁPIDA', 'CONFIDENCIAL', 'AGENTE LOCAL EN CONROE', 'FIANZAS DE CÁRCEL']
    : ['MONTGOMERY COUNTY', 'LICENSE #0111', 'AVAILABLE 24/7', 'BILINGUAL', 'FLEXIBLE PAYMENT PLANS', 'STEVE CRUZ · AGENT', 'FAST RESPONSE', 'CONFIDENTIAL', 'LOCAL CONROE OFFICE', 'HABLAMOS ESPAÑOL'];

  const stats = [
    { value: '24/7', label: lang === 'es' ? 'Disponible' : 'Availability' },
    { value: '#0111', label: lang === 'es' ? 'Licencia TX' : 'TX License' },
    { value: '9', label: lang === 'es' ? 'Ciudades Cubiertas' : 'Cities Covered', suffix: '+' },
    { value: '30', label: lang === 'es' ? 'Min. Respuesta' : 'Min. Response', suffix: ' MIN' },
  ];

  const authority = [
    { icon: <Zap size={26} />, title: lang === 'es' ? 'RESPUESTA INMEDIATA' : 'FAST RESPONSE',
      text: lang === 'es' ? 'Un agente real contesta 24/7. Comenzamos el papeleo al confirmar el nombre.' : 'A real agent answers 24/7. We start paperwork the moment you confirm the defendant\'s name.' },
    { icon: <Shield size={26} />, title: lang === 'es' ? 'LICENCIADO Y ASEGURADO' : 'LICENSED & BONDED',
      text: lang === 'es' ? 'Agente autorizado Steve Cruz, Licencia #0111. Basado en Conroe — agente local, no una línea de llamadas.' : 'Licensed agent Steve Cruz, TX License #0111. Local Conroe office — a real agent, not a call center.' },
    { icon: <Handshake size={26} />, title: lang === 'es' ? 'AGENTE LOCAL — NO CALL CENTER' : 'LOCAL AGENT — NOT A CALL CENTER',
      text: lang === 'es' ? 'Basados en Conroe. Conocemos la cárcel del Condado de Montgomery de primera mano.' : 'Based in Conroe. We know the Montgomery County Jail process firsthand — no franchise script.' },
    { icon: <Languages size={26} />, title: lang === 'es' ? 'SERVICIO BILINGÜE' : 'BILINGUAL SERVICE',
      text: lang === 'es' ? 'Ingles y Español en cada llamada. Le atendemos en el idioma que prefiera.' : 'English and Español on every call. We handle your case in the language you\'re most comfortable in.' },
    { icon: <Landmark size={26} />, title: lang === 'es' ? 'PROCESO DEL CONDADO' : 'COUNTY-SPECIFIC PROCESS',
      text: lang === 'es' ? 'Cárcel, juzgado, magistrado — conocemos el proceso de Conroe al detalle.' : 'Jail, courthouse, magistrate — we know the Conroe booking-to-release process cold.' },
    { icon: <HeartHandshake size={26} />, title: lang === 'es' ? 'FAMILIA, NO SORPRESAS' : 'FAMILY, NO SURPRISES',
      text: lang === 'es' ? 'Le decimos el costo exacto y los pasos antes de firmar cualquier cosa.' : 'We tell you the exact cost and every step before you sign anything. No pressure. No surprises.' },
  ];

  const path = [
    { n: '01', title: lang === 'es' ? 'LLAMADA' : 'CALL',
      text: lang === 'es' ? 'Marque nuestra línea 24/7. Un agente real contesta en minutos.' : 'Dial our 24/7 line. A real agent picks up — usually within minutes.' },
    { n: '02', title: lang === 'es' ? 'VERIFICACIÓN' : 'VERIFY',
      text: lang === 'es' ? 'Confirmamos el nombre, la fecha de nacimiento y el monto de la fianza.' : 'We confirm the defendant\'s name, DOB and bond amount together.' },
    { n: '03', title: lang === 'es' ? 'PAPELEO' : 'PAPERWORK',
      text: lang === 'es' ? 'Preparamos el formulario y explicamos cada paso en su idioma.' : 'We prep the paperwork and walk you through every step in your language.' },
    { n: '04', title: lang === 'es' ? 'A CASA' : 'HOMEBOUND',
      text: lang === 'es' ? 'La fianza se registra y su ser querido regresa a casa.' : 'The bond is posted and your loved one heads home.' },
  ];

  const testimonials = [
    { text: lang === 'es'
        ? 'Llamamos a las 3 de la mañana asustados. Nos contestaron al primer timbre y en horas mi hermano estaba en casa. Steve nos explicó todo con paciencia.'
        : 'We called at 3 AM terrified. They answered on the first ring and within hours my brother was home. Steve walked us through everything with patience.',
      name: 'Maria R.', role: 'Conroe, TX', initials: 'MR' },
    { text: lang === 'es'
        ? 'Después de llamar a tres oficinas, Amigo\'s fue la única que atendió inmediatamente y respondió en español sin dudar. Profesionales de verdad.'
        : 'After calling three other offices, Amigo\'s was the only one who picked up immediately and answered in Spanish without missing a beat. True professionals.',
      name: 'Roberto & Elena V.', role: 'Willis, TX', initials: 'RV' },
    { text: lang === 'es'
        ? 'No sabíamos nada de fianzas. Nos dijeron el costo exacto por adelantado, sin sorpresas, sin presión. Mi hijo volvió a casa esa misma noche.'
        : 'We knew nothing about bail. They told us the exact cost up front — no surprises, no pressure. My son came home that same night.',
      name: 'Patricia D.', role: 'The Woodlands, TX', initials: 'PD' },
  ];

  const featuredCities = CITIES.slice(0, 6);

  return (
    <div className="amigos-app" ref={gsapScope}>
      <div id="amg-scrollbar" className="amg-scrollbar" />
      <SEO
        title="Amigo's Bail Bonds Conroe TX | 24/7 Montgomery County Bail Bondsman | Se Habla Español | License #0111"
        description="24/7 bail bonds in Conroe, Texas. Licensed agent Steve Cruz (TX #0111) handles Montgomery County Jail releases fast. Bilingual English/Español. Flexible payment plans. Call (936) 828-2500 now."
        path="/"
        keywords="bail bonds Conroe TX, Montgomery County bail bonds, bail bondsman Conroe, 24 hour bail bonds Montgomery County, fianzas Conroe, bail bonds The Woodlands, bail bonds Willis TX, bail bonds Magnolia TX, Montgomery County Jail bail bonds, Spanish speaking bail bondsman Texas, licensed bail agent 0111, Steve Cruz bail bonds, Amigos bail bonds Conroe, affordable bail bonds Montgomery County, emergency bail bonds Texas, immigration bond Montgomery County"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            '@id': 'https://amigosbailbonds.com/#business',
            name: "Amigo's Bail Bonds",
            alternateName: "Amigos Bail Bonds",
            legalName: "Amigo's Bail Bonds, Steve Cruz License #0111",
            description: "Licensed 24/7 bail bond service based in Conroe, Texas. Serving Montgomery County and surrounding areas with fast, confidential, bilingual bail bond help.",
            url: 'https://amigosbailbonds.com',
            telephone: '+19368282500',
            priceRange: '$$',
            image: 'https://amigosbailbonds.com/brand/amigos-logo.png',
            address: { '@type': 'PostalAddress', streetAddress: 'Conroe, TX', addressLocality: 'Conroe', addressRegion: 'TX', postalCode: '77301', addressCountry: 'US' },
            geo: { '@type': 'GeoCoordinates', latitude: '30.3118', longitude: '-95.4560' },
            areaServed: [
              { '@type': 'AdministrativeArea', name: 'Montgomery County, TX' },
              ...CITIES.map((c) => ({ '@type': 'City', name: c.en.name })),
            ],
            openingHoursSpecification: [{ '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'], opens: '00:00', closes: '23:59' }],
            hasOfferCatalog: {
              '@type': 'OfferCatalog', name: 'Bail Bond Services',
              itemListElement: [
                { '@type': 'Offer', name: 'Montgomery County Jail Bail Bonds' },
                { '@type': 'Offer', name: '24/7 Emergency Bail Service' },
                { '@type': 'Offer', name: 'Bilingual Bail Bonds (English & Español)' },
                { '@type': 'Offer', name: 'Flexible Payment Plans' },
                { '@type': 'Offer', name: 'Immigration Bonds' },
              ],
            },
            aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '87', bestRating: '5' },
            sameAs: [],
          },
          {
            '@context': 'https://schema.org', '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: 'How fast can you post a bail bond in Montgomery County?',
                acceptedAnswer: { '@type': 'Answer', text: 'Most Montgomery County Jail bonds are posted within a few hours of paperwork completion. Call (936) 828-2500 the moment you have the defendant\u2019s name and date of birth and we can start immediately, 24/7.' } },
              { '@type': 'Question', name: 'Do you speak Spanish?',
                acceptedAnswer: { '@type': 'Answer', text: 'Yes \u2014 Amigo\u2019s Bail Bonds handles every call, form, and paperwork step in either English or Español at no additional cost. Se habla Español en cada llamada.' } },
              { '@type': 'Question', name: 'How much does a bail bond cost in Texas?',
                acceptedAnswer: { '@type': 'Answer', text: 'Texas bail bond premiums are set by state law. We tell you the exact cost up front before you sign anything and offer flexible payment plans based on your situation \u2014 no hidden fees, no surprises.' } },
              { '@type': 'Question', name: 'Where is Amigo\u2019s Bail Bonds located?',
                acceptedAnswer: { '@type': 'Answer', text: 'We are based in Conroe, Texas \u2014 the Montgomery County seat, right next to the Montgomery County Jail and courthouse. Licensed agent Steve Cruz (TX License #0111).' } },
            ],
          },
        ]}
      />
      <div className="film-grain" />

      <EmergencyStrip />
      <CinematicNav lang={lang} />

      {/* ─── HERO ─── */}
      <div style={{ position: 'relative' }}>
        <JailBarHero
          bgImage="https://images.unsplash.com/photo-1633524578506-60ca707de140?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
          badge={lang === 'es' ? '24/7 · BILINGÜE · CONDADO DE MONTGOMERY' : '24/7 · BILINGUAL · MONTGOMERY COUNTY'}
          title={lang === 'es' ? 'TU AMIGO' : 'YOUR TRUSTED'}
          titleAccent={lang === 'es' ? 'DE CONFIANZA AFUERA' : 'AMIGO ON THE OUTSIDE'}
          subtitle={lang === 'es'
            ? <>Ayuda de fianzas rápida y confidencial para el Condado de Montgomery. Disponible 24/7. <em>Hablamos Español.</em></>
            : <>Fast, confidential bail bond help for Montgomery County. Available 24/7. <em>Hablamos Español.</em></>}
          ctas={
            <>
              <Link to="/start-a-bond" className="btn-red" data-testid="hero-start-bond-btn" onClick={() => track('hero_cta_click', { cta: 'start_bond' })}>
                <FileSignature size={16} /> {lang === 'es' ? 'INICIAR FIANZA' : 'START A BOND'}
              </Link>
              <a href="tel:9368282500" className="btn-brass" data-testid="hero-call-now-btn" onClick={() => track('hero_cta_click', { cta: 'call_now' })}>
                <Phone size={16} /> {lang === 'es' ? 'LLAMAR AHORA' : 'CALL NOW'}
              </a>
            </>
          }
          scrollHintText={lang === 'es' ? 'DESLIZAR' : 'SCROLL'}
          testId="home-hero"
        />
        <div className="hero-trust-row" data-testid="hero-trust-row">
          {trustChips.map((c, i) => (
            <span key={i} className="hero-trust-chip">
              {c.icon}
              {lang === 'es' ? c.es : c.en}
            </span>
          ))}
        </div>
      </div>

      {/* ─── MARQUEE ─── */}
      <Marquee items={marqueeItems} />

      {/* ─── ANIMATED STATS ─── */}
      <section className="cn-sec--void" style={{ padding: 0 }} data-testid="cn-stats-section">
        <div className="cn-stats">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="cn-stat" data-testid={`cn-stat-${i}`}>
                <div className="cn-stat__value">
                  <Counter value={s.value} suffix={s.suffix || ''} />
                </div>
                <div className="cn-stat__label">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="cn-glow-divider" />

      {/* ─── AUTHORITY / WHY US ─── */}
      <section className="cn-sec cn-sec--deep" data-testid="cn-why-section">
        <Reveal>
          <div className="cn-sec__header">
            <span className="cn-sec__label">{lang === 'es' ? 'POR QUÉ AMIGOS' : 'WHY AMIGO\u2019S'}</span>
            <h2 className="cn-sec__title">
              {lang === 'es' ? 'AUTORIDAD LOCAL,' : 'LOCAL AUTHORITY,'} <span className="cn-sec__title-accent">{lang === 'es' ? 'RESPUESTA REAL' : 'REAL RESPONSE'}</span>
            </h2>
            <p className="cn-sec__subtitle">
              {lang === 'es'
                ? 'Somos su agente de fianzas licenciado con sede en Conroe. Sin call center, sin franquicia, sin guiones — solo un agente real que conoce el proceso del Condado de Montgomery.'
                : 'We\'re your Conroe-based, licensed bail agent. No call center. No franchise. No script — just a real agent who knows the Montgomery County process cold.'}
            </p>
          </div>
        </Reveal>
        <div className="cn-authority">
          {authority.map((c, i) => (
            <div key={i} className="cn-auth-card" data-testid={`cn-auth-${i}`}>
              <div className="cn-auth-card__icon">{c.icon}</div>
              <h3 className="cn-auth-card__title">{c.title}</h3>
              <p className="cn-auth-card__text">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="cn-glow-divider" />

      {/* ─── THE PATH HOME ─── */}
      <section className="cn-sec cn-sec--surface" data-testid="cn-path-section">
        <Reveal>
          <div className="cn-sec__header">
            <span className="cn-sec__label">{lang === 'es' ? 'EL CAMINO A CASA' : 'THE PATH HOME'}</span>
            <h2 className="cn-sec__title">
              {lang === 'es' ? 'CUATRO PASOS.' : 'FOUR STEPS.'} <span className="cn-sec__title-red">{lang === 'es' ? 'DE VUELTA A CASA.' : 'BACK HOME.'}</span>
            </h2>
            <p className="cn-sec__subtitle">
              {lang === 'es' ? 'Un proceso claro y sin sorpresas. Le explicamos cada paso antes de firmar nada.' : 'A clear, no-surprise process. We explain every step before you sign anything.'}
            </p>
          </div>
        </Reveal>
        <div className="cn-path">
          {path.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="cn-path__step" data-testid={`cn-path-step-${i}`}>
                <div className="cn-path__num">{s.n}</div>
                <h3 className="cn-path__title">{s.title}</h3>
                <p className="cn-path__text">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="cn-glow-divider" />

      {/* ─── ABOUT STEVE CRUZ + CREDENTIALS ─── */}
      <section className="cn-sec cn-sec--deep" data-testid="cn-about-section">
        <div className="cn-about">
          <Reveal>
            <div>
              <span className="cn-about__eyebrow">{lang === 'es' ? 'CONOZCA A SU AGENTE' : 'MEET YOUR AGENT'}</span>
              <h2 className="cn-about__title">
                {lang === 'es' ? 'STEVE CRUZ — SU AMIGO EN CONROE' : 'STEVE CRUZ — YOUR AMIGO IN CONROE'}
              </h2>
              <p className="cn-about__body">
                {lang === 'es'
                  ? <>Amigo&apos;s Bail Bonds está licenciada bajo el agente <em>Steve Cruz (Licencia #0111)</em>. Basado en Conroe, Steve conoce el sistema del Condado de Montgomery de primera mano — la cárcel, el juzgado y el magistrado.</>
                  : <>Amigo&apos;s Bail Bonds is licensed under agent <em>Steve Cruz (License #0111)</em>. Based in Conroe, Steve knows the Montgomery County system firsthand — the jail, the courthouse, the magistrate.</>}
              </p>
              <p className="cn-about__body">
                {lang === 'es'
                  ? 'No somos una franquicia nacional leyendo un guión. Somos su agente local respondiendo el teléfono personalmente, en el idioma que prefiera, cuando más lo necesita.'
                  : "We're not a national franchise reading a script. We're your local agent answering the phone personally — in the language you prefer — when it matters most."}
              </p>
              <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginTop: '1.6rem' }}>
                <Link to="/about" className="btn-brass" data-testid="about-more-link">
                  {lang === 'es' ? 'Sobre Amigos' : 'About Amigo\u2019s'} <ChevronRight size={16} />
                </Link>
                <Link to="/start-a-bond" className="btn-red" data-testid="about-start-link">
                  <FileSignature size={15} /> {lang === 'es' ? 'Iniciar Fianza' : 'Start a Bond'}
                </Link>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="cn-cred" data-testid="cn-credentials">
              <h3 className="cn-cred__title">{lang === 'es' ? 'CREDENCIALES' : 'CREDENTIALS'}</h3>
              {[
                { label: lang === 'es' ? 'Agente Licenciado' : 'Licensed Agent', value: 'License #0111 · TX' },
                { label: lang === 'es' ? 'Sede' : 'Home Office', value: 'Conroe, Montgomery County, TX' },
                { label: lang === 'es' ? 'Servicio Bilingüe' : 'Bilingual Service', value: lang === 'es' ? 'Inglés y Español' : 'English & Español' },
                { label: lang === 'es' ? 'Disponibilidad' : 'Availability', value: '24 / 7 · 365' },
                { label: lang === 'es' ? 'Protección de Datos' : 'Data Protection', value: 'TITN Aegis Security' },
              ].map((r, i) => (
                <div key={i} className="cn-cred__row">
                  <div className="cn-cred__row-dot" />
                  <div>
                    <div className="cn-cred__row-label">{r.label}</div>
                    <div className="cn-cred__row-value">{r.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <div className="cn-glow-divider" />

      {/* ─── TESTIMONIALS ─── */}
      <section className="cn-sec cn-sec--surface" data-testid="cn-testimonials-section">
        <Reveal>
          <div className="cn-sec__header">
            <span className="cn-sec__label">{lang === 'es' ? 'TESTIMONIOS' : 'TESTIMONIALS'}</span>
            <h2 className="cn-sec__title">
              {lang === 'es' ? 'FAMILIAS QUE' : 'FAMILIES WHO'} <span className="cn-sec__title-accent">{lang === 'es' ? 'NOS CONFIARON' : 'TRUSTED US'}</span>
            </h2>
            <p className="cn-sec__subtitle">
              {lang === 'es' ? 'Vecinos del Condado de Montgomery que nos llamaron en su peor momento.' : 'Montgomery County neighbors who called us on their worst night.'}
            </p>
          </div>
        </Reveal>
        <div className="cn-testimonials">
          {testimonials.map((t, i) => (
            <div key={i} className="cn-testimonial" data-testid={`cn-testimonial-${i}`}>
              <div className="cn-testimonial__stars">
                {Array.from({ length: 5 }).map((_, j) => <Star key={j} size={15} fill="currentColor" />)}
              </div>
              <p className="cn-testimonial__text">&ldquo;{t.text}&rdquo;</p>
              <div className="cn-testimonial__author">
                <div className="cn-testimonial__avatar">{t.initials}</div>
                <div>
                  <div className="cn-testimonial__name">{t.name}</div>
                  <div className="cn-testimonial__role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="cn-glow-divider" />

      {/* ─── SERVICE AREAS ─── */}
      <section className="cn-sec cn-sec--deep" data-testid="cn-areas-section">
        <Reveal>
          <div className="cn-sec__header">
            <span className="cn-sec__label">{lang === 'es' ? 'ÁREAS DE SERVICIO' : 'SERVICE AREAS'}</span>
            <h2 className="cn-sec__title">
              {lang === 'es' ? 'CUBRIENDO' : 'COVERING'} <span className="cn-sec__title-accent">{lang === 'es' ? 'EL CONDADO' : 'THE COUNTY'}</span>
            </h2>
            <p className="cn-sec__subtitle">
              {lang === 'es' ? 'Desde Conroe hasta cada rincón. Cobertura local con conocimiento local.' : 'From Conroe to every corner. Local coverage with local knowledge.'}
            </p>
          </div>
        </Reveal>
        <div className="cn-areas">
          {featuredCities.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.06}>
              <Link to={`/areas/${c.slug}`} className="cn-area" data-testid={`cn-area-${c.slug}`}
                onClick={() => track('service_area_view', { city: c.slug, from: 'home_cinematic' })}>
                <div className="cn-area__county"><MapPin size={11} /> {c[lang].county}</div>
                <h3 className="cn-area__name">{c[lang].name}</h3>
                <p className="cn-area__intro">
                  {c[lang].intro.length > 120 ? c[lang].intro.slice(0, 120) + '\u2026' : c[lang].intro}
                </p>
                <span className="cn-area__arrow">
                  {lang === 'es' ? 'Ver detalles' : 'View details'} <ArrowRight size={13} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
          <Link to="/areas" className="btn-ghost-dark" data-testid="cn-view-all-areas">
            {lang === 'es' ? 'VER TODAS LAS ÁREAS' : 'VIEW ALL SERVICE AREAS'} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <div className="cn-glow-divider" />

      {/* ─── BILINGUAL + FINANCING SPLIT ─── */}
      <section className="cn-sec cn-sec--surface" data-testid="cn-split-section">
        <div className="cn-split">
          <Reveal>
            <div className="cn-split__panel" data-testid="cn-split-bilingual">
              <span className="cn-split__eyebrow"><Languages size={14} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} /> {lang === 'es' ? 'BILINGÜE, EN CADA LLAMADA' : 'BILINGUAL, ON EVERY CALL'}</span>
              <h3 className="cn-split__title">{lang === 'es' ? 'HABLAMOS SU IDIOMA' : 'WE SPEAK YOUR LANGUAGE'}</h3>
              <p className="cn-split__body">
                {lang === 'es'
                  ? 'Cada llamada, cada formulario, cada conversación — disponible en Español o Inglés. Sin cargos adicionales, sin retrasos.'
                  : 'Every call, every form, every conversation — available in Español or English. No extra fees, no delays. The language you\'re most comfortable in.'}
              </p>
              <ul className="cn-split__list">
                <li><CheckCircle2 size={16} /> {lang === 'es' ? 'Agentes bilingües 24/7' : '24/7 bilingual agents'}</li>
                <li><CheckCircle2 size={16} /> {lang === 'es' ? 'Formularios en ambos idiomas' : 'Forms available in both languages'}</li>
                <li><CheckCircle2 size={16} /> {lang === 'es' ? 'Interpretación cortés y clara' : 'Courteous, clear interpretation'}</li>
              </ul>
              <a href="tel:9368282500" className="btn-brass" data-testid="cn-bilingual-call">
                <Phone size={15} /> {lang === 'es' ? 'Llamar Ahora' : 'Call Now'}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="cn-split__panel" data-testid="cn-split-financing">
              <span className="cn-split__eyebrow"><Award size={14} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} /> {lang === 'es' ? 'PAGOS FLEXIBLES' : 'FLEXIBLE PAYMENTS'}</span>
              <h3 className="cn-split__title">{lang === 'es' ? 'PLANES DE PAGO' : 'PAYMENT PLANS'}</h3>
              <p className="cn-split__body">
                {lang === 'es'
                  ? 'Le decimos el costo exacto antes de firmar. Ofrecemos planes de pago flexibles según su situación — sin sorpresas, sin presión.'
                  : "We tell you the exact cost up front — before you sign anything. Flexible payment plans are available based on your situation. No surprises. No pressure."}
              </p>
              <ul className="cn-split__list">
                <li><CheckCircle2 size={16} /> {lang === 'es' ? 'Costo transparente por adelantado' : 'Transparent up-front pricing'}</li>
                <li><CheckCircle2 size={16} /> {lang === 'es' ? 'Planes ajustados a su situación' : 'Plans tailored to your situation'}</li>
                <li><CheckCircle2 size={16} /> {lang === 'es' ? 'Sin cargos ocultos' : 'No hidden fees'}</li>
              </ul>
              <Link to="/financing" className="btn-brass" data-testid="cn-financing-link">
                {lang === 'es' ? 'Opciones de Pago' : 'Payment Options'} <ChevronRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="cn-glow-divider" />

      {/* ─── FINAL CTA ─── */}
      <section className="cn-final" data-testid="cn-final-cta">
        <div className="cn-final__bg" />
        <div className="cn-final__content">
          <Reveal>
            <h2 className="cn-final__title">
              {lang === 'es' ? 'NO ESPERE.' : 'DON\u2019T WAIT.'}<br />
              <span className="accent">{lang === 'es' ? 'LLÁMENOS AHORA.' : 'CALL NOW.'}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="cn-final__sub">
              {lang === 'es'
                ? 'Cada minuto importa. Un agente real está listo para contestar y comenzar el proceso.'
                : 'Every minute matters. A real agent is standing by to answer and start the process.'}
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="cn-final__ctas">
              <a href="tel:9368282500" className="cn-final__phone" data-testid="cn-final-call"
                 onClick={() => track('final_cta_call', { location: 'home_cinematic' })}>
                <Phone size={26} /> (936) 828-2500
              </a>
              <Link to="/start-a-bond" className="btn-brass" style={{ padding: '1.15rem 2rem', fontSize: '1rem' }} data-testid="cn-final-start"
                    onClick={() => track('final_cta_start', { location: 'home_cinematic' })}>
                <FileSignature size={16} /> {lang === 'es' ? 'Iniciar Fianza' : 'Start a Bond'}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CinematicFooter />
    </div>
  );
};

export default CinematicHome;
